import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, AlertCircle, Mail, ChevronRight } from 'lucide-react';
import PasswordRequirements from '../components/PasswordRequirements';
import { motion, AnimatePresence } from 'framer-motion';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenFromUrl = searchParams.get('token');

    if (tokenFromUrl) {
      verifyResetToken(tokenFromUrl);
    }
  }, [location]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al enviar el enlace');
      }

      setSuccess(true);
      setStep('check-email');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyResetToken = async (resetToken) => {
    try {
      const response = await fetch(`https://backend-production-1786.up.railway.app/api/auth/verify-reset-token?token=${resetToken}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Token inválido');
      }

      setEmail(data.email);
      setToken(resetToken);
      setStep('new-password');
    } catch (error) {
      setError('El enlace ha expirado o no es válido');
      setStep('request');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    const requirements = [
      newPassword.length >= 8,
      /[A-Z]/.test(newPassword),
      /[a-z]/.test(newPassword),
      /[0-9]/.test(newPassword),
      /[!@#$%^&¿?*]/.test(newPassword)
    ];

    if (!requirements.every(req => req)) {
      setError('La contraseña no cumple con todos los requisitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al restablecer la contraseña');
      }

      navigate('/login', { 
        state: { 
          message: 'Contraseña restablecida exitosamente',
          type: 'success'
        } 
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="min-h-screen bg-indigo-900 flex items-center justify-center px-4 py-12">
        <motion.div 
          className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 space-y-6"
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ 
            type: "spring", 
            damping: 15,
            duration: 0.6
          }}
        >
          <motion.div 
            className="text-center"
            variants={fadeInUp}
          >
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-4 rounded-full mb-6 shadow-md w-16 h-16 flex items-center justify-center mx-auto">
              <Lock className="w-8 h-8 text-indigo-600" />
            </div>
            <h2 className="text-3xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700">
              Restablecer Contraseña
            </h2>
            
            {error && (
              <motion.div 
                className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mt-4 flex items-center"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <AlertCircle className="mr-2 text-red-500" size={20} />
                <span>{error}</span>
              </motion.div>
            )}
          </motion.div>

          <AnimatePresence mode="wait">
            {step === 'request' && (
              <motion.form 
                key="request-form"
                onSubmit={handleRequestReset} 
                className="space-y-4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative group">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 
                                   transition-colors group-hover:text-indigo-500" size={20} />
                  <input
                    type="email"
                    placeholder="Correo electrónico"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border-2 border-indigo-100 rounded-xl bg-white
                             transition-all duration-300
                             focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                             group-hover:border-indigo-200"
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white 
                           py-3 rounded-xl font-medium transform transition-all duration-300
                           hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg
                           active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed
                           relative overflow-hidden group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="relative z-10 inline-flex items-center">
                    {loading ? 'Enviando...' : 'Enviar Enlace de Restablecimiento'}
                    {!loading && <ChevronRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />}
                  </span>
                </motion.button>
              </motion.form>
            )}

            {step === 'check-email' && (
              <motion.div 
                key="check-email"
                className="text-center space-y-4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6">
                  <Mail className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Revisa tu correo
                  </h3>
                  <p className="text-gray-600">
                    Hemos enviado un enlace de restablecimiento de contraseña a <strong>{email}</strong>. 
                    Por favor, revisa tu bandeja de entrada y la carpeta de spam.
                  </p>
                  <p className="text-sm text-gray-500 mt-4">
                    El enlace expirará en 30 minutos.
                  </p>
                </div>
                <motion.button
                  onClick={() => setStep('request')}
                  className="text-indigo-500 font-medium hover:text-indigo-700 hover:underline inline-flex items-center group"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <span>¿No recibiste el enlace? Intentar nuevamente</span>
                </motion.button>
              </motion.div>
            )}

            {step === 'new-password' && (
              <motion.form 
                key="new-password-form"
                onSubmit={handleResetPassword} 
                className="space-y-4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative group">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 
                                  transition-colors group-hover:text-indigo-500" size={20} />
                  <input
                    type="password"
                    placeholder="Nueva Contraseña"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border-2 border-indigo-100 rounded-xl bg-white
                             transition-all duration-300
                             focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                             group-hover:border-indigo-200"
                  />
                </div>

                <div className="relative group">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 
                                  transition-colors group-hover:text-indigo-500" size={20} />
                  <input
                    type="password"
                    placeholder="Confirmar Nueva Contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border-2 border-indigo-100 rounded-xl bg-white
                             transition-all duration-300
                             focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                             group-hover:border-indigo-200"
                  />
                </div>

                <PasswordRequirements password={newPassword} />

                <motion.button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white 
                           py-3 rounded-xl font-medium transform transition-all duration-300
                           hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg
                           active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed
                           relative overflow-hidden group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="relative z-10 inline-flex items-center">
                    {loading ? 'Restableciendo...' : 'Restablecer Contraseña'}
                    {!loading && <ChevronRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />}
                  </span>
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ResetPassword;